import type { ViewerDeviceRuntime } from "../types";

export function isRemoteMediaAssetUrl(url: string): boolean {
  return /^https?:\/\//i.test(url.trim());
}

/** Resolves packagePath the same way offline export assets are addressed in the viewer. */
export function resolvePackageAssetPath(packagePath: string): string {
  const normalizedPackagePath = packagePath.replace(/\/$/, "");

  if (
    normalizedPackagePath.startsWith("/") ||
    normalizedPackagePath.startsWith("http") ||
    normalizedPackagePath.startsWith("file")
  ) {
    return normalizedPackagePath;
  }

  if (typeof window !== "undefined" && window.location?.pathname) {
    const basePath = window.location.pathname.substring(
      0,
      window.location.pathname.lastIndexOf("/") + 1,
    );
    return `${basePath}${normalizedPackagePath}`.replace(/\/$/, "");
  }

  return normalizedPackagePath;
}

/** Extracts the offline package image file name from a Pearson media or image URL. */
export function getOfflinePackageImageFileName(rawUrl: string): string | null {
  if (!rawUrl) {
    return null;
  }

  const lastSegment = rawUrl.substring(rawUrl.lastIndexOf("/") + 1);
  if (!lastSegment) {
    return null;
  }

  if (lastSegment.includes("?mimeType=")) {
    const [fileName, extension] = lastSegment.split("?mimeType=");
    return extension ? `${fileName}.${extension}` : null;
  }

  if (/_video\.true\/|_pmd\.true\//.test(rawUrl)) {
    return lastSegment.includes(".") ? lastSegment : `${lastSegment}.jpg`;
  }

  if (/\.(jpg|jpeg|png|gif|webp|bmp|svg|tiff)$/i.test(lastSegment)) {
    return lastSegment;
  }

  // Bare Pearson mediaplayer asset IDs, e.g. .../assets/bzca4e_2_5_cp8
  if (/mediaplayer\.pearsoncmg\.com\/assets\//i.test(rawUrl)) {
    const assetId = lastSegment.split("?")[0];
    if (assetId && assetId !== "_video.true" && assetId !== "_pmd.true" && !assetId.includes(".")) {
      return `${assetId}.jpg`;
    }
  }

  return null;
}

/** Builds a local package image path under assets/images/original. */
export function getOfflinePackageImageUrl(packagePath: string, rawUrl: string): string | null {
  const resolvedPackagePath = resolvePackageAssetPath(packagePath);
  const trimmedUrl = rawUrl.trim();

  if (!trimmedUrl) {
    return null;
  }

  if (trimmedUrl.startsWith(resolvedPackagePath) && trimmedUrl.includes("/assets/images/")) {
    return trimmedUrl;
  }

  if (!isRemoteMediaAssetUrl(trimmedUrl) && trimmedUrl.includes("/assets/images/")) {
    return trimmedUrl;
  }

  const imageName = getOfflinePackageImageFileName(trimmedUrl);
  if (!imageName) {
    return null;
  }

  return `${resolvedPackagePath}/assets/images/original/${imageName}`;
}

export function shouldUseOfflinePackageImages(viewerDevice?: ViewerDeviceRuntime): boolean {
  return Boolean(viewerDevice?.packagePath);
}

/** When a package path is set, map remote image URLs to the offline package; otherwise return unchanged. */
export function resolveImageUrlForDevice(
  rawUrl: string | undefined | null,
  viewerDevice?: ViewerDeviceRuntime,
): string {
  const trimmedUrl = rawUrl?.trim() ?? "";
  if (!trimmedUrl) {
    return "";
  }

  if (!shouldUseOfflinePackageImages(viewerDevice)) {
    return trimmedUrl;
  }

  const packageUrl = getOfflinePackageImageUrl(viewerDevice!.packagePath!, trimmedUrl);
  return packageUrl ?? trimmedUrl;
}
